// @flow
import last from 'lodash/last';
import uniq from 'lodash/uniq';
import orderBy from 'lodash/orderBy';
import isMatch from 'lodash/isMatch';
import isEqual from 'lodash/isEqual';
import randomMC from 'random-material-color';

import ConversationActionTypes from '../action-types/conversation';
import { assertScribeConversationStateConsistency } from '../utils/consistence';
import { computeTextFormLastMutation, getText, mutate as mutateTranscript, getLang } from '../utils/scribeUtils';
import { getSearchValue } from '../utils/http';
import availableLangs from '../../assets/langs.json';
import type { JoiningStatus } from '../types/conversation';

const isProd =
  window.location.href.indexOf('app.avascribe.com') > -1 ||
  window.location.href.indexOf('app.ava.me') > -1 ||
  window.location.href.indexOf('web.ava.me') > -1;

export type State = {
  availableLangs: {
    [key: string]: { fr: string, en: string, languageCode: string },
  },
  allMicsSelected: Array<string>,
  lang: string,
  conversations: Array<Object>,
  langs: Array<string>,
  +micIdSelected?: string,
  +availableMics?: Array<any>,
  backendKnowUpdateField: boolean,
  +conversationRequested?: boolean,
  +conversationStarted?: boolean,
  +isInConversation?: boolean,
  +conversationInputIsEdited?: boolean,
  +conversationEnded?: boolean,
  +conversationEndedSuccessfully?: boolean,
  +conversationEndedMessage?: string,
  +connectionLost?: boolean,
  +url?: string,
  +tags: Object,
  +conversationOrigin?: string,
  +transcripts: TranscriptMap,
  muted: boolean,
  +speakers: Object,
  +transcriptsFinal: Array<string>,
  +transcriptsCurrent: Array<string>,
  // How far into the transcripts we have seen each participant click. This is useful to see how far the scribe
  // has touched the transcript, with the assumption that if a scribe tab-ed or clicked onto a word, then everything
  // before then is *checked*.
  furthestObservedCursors: { [participantHash: string]: { tid: string, index: number } },
  +sampleRateHertz?: number,
  +chunkLengthMs?: number,
  isBoosting: boolean,
  +id?: string,
  recording: boolean,
  hasRecorded: boolean,
  connectionHash?: string,
  token?: string,
  status: Object,
  scribe: string,
  speakerListened?: string,
  lastMutationReceivedTimestampMs?: number,
  loaded: boolean,
  reconnectionWsUrl?: string,
  ui: {
    prevSelectedWordIndex?: number,
    selectedWordIndex?: number,
    selectedTranscriptIndex?: string,
    selectedTranscriptCaretPosition?: string,
    selectedIndexFrom?: number,
    selectedIndexTo?: number,
    selectionLength: number,
    selectedWordInputValue?: string, // The text for this shuold probably be changed
    selectedSearchMatchIndex: number,
    lastEditedWordIndex: number,
    keyboardInputShown: boolean,
  },
  broadcastToZoomToken?: string,
  onlyInternalAudio?: boolean,
  editedWords: { text: string, transcriptionId: string }[],
  isInviteCardOpen: boolean,
  joiningStatus: JoiningStatus,
};

const updateAuthorShow = state => {
  const transcriptsToUpdate = {};
  let lt;
  // eslint-disable-next-line
  for (let i = 0; i < state.transcriptsCurrent.length; i++) {
    const transcript = state.transcripts[state.transcriptsCurrent[i]];
    const ltCandidate = state.transcripts[state.transcriptsCurrent[i - 1]];
    if (ltCandidate && getText(ltCandidate, state.lang)) {
      lt = ltCandidate;
    }
    if (i === 0) {
      lt = state.transcripts[state.transcriptsFinal[state.transcriptsFinal.length - 1]];
    }
    const shouldShow = !lt || transcript.author !== lt.author || transcript.trackName !== lt.trackName;
    if (transcript.authorShow !== shouldShow) {
      transcriptsToUpdate[transcript.id] = {
        ...transcript,
        authorShow: shouldShow,
      };
    }
  }
  return {
    ...state,
    transcripts: {
      ...state.transcripts,
      ...transcriptsToUpdate,
    },
  };
};

const mutate = (
  state: State,
  action: {
    mutation: ScribeTranscriptMutation,
    asr: boolean,
    transcriptId: any,
  }
) => {
  const transcript = state.transcripts[action.transcriptId];
  const { transcript: newTranscript, indexWord: newIndexWord } = mutateTranscript(
    transcript,
    state.ui.selectedWordIndex || 0,
    action.mutation,
    action.asr
  );
  const words = getText(newTranscript, state.lang).split(' ');

  const cursors = transcript.cursors || [];
  const currentMutation: ScribeTranscriptMutation = action.mutation;
  // $FlowFixMe
  let furthestCursorForCurrentMutator =
    currentMutation.data.mutatorHash && state.furthestObservedCursors[currentMutation.data.mutatorHash];
  let currentTouchedIndex;
  let cursorsUpdated = false;
  if (currentMutation.type === 'cursor') {
    // $FlowFixMe
    currentTouchedIndex = { tid: action.transcriptId, index: currentMutation.data.index };
    if (state.connectionHash !== currentMutation.data.mutatorHash) {
      cursorsUpdated = true;
      // $FlowFixMe
      const indexToChange = (cursors || []).map(cursor => cursor.mutatorHash).indexOf(currentMutation.data.mutatorHash);
      if (indexToChange !== -1) {
        cursors[indexToChange] = currentMutation.data;
      } else {
        cursors.push(currentMutation.data);
      }
    }
  }
  if (
    (action.mutation.type === 'insert' || action.mutation.type === 'delete') &&
    action.mutation.data.lang === state.lang
  ) {
    // $FlowFixMe
    currentTouchedIndex = { tid: action.transcriptId, index: currentMutation.data.index };
    for (let i = 0; i < cursors.length; i += 1) {
      const movedWord = computeTextFormLastMutation(
        getText(transcript, action.mutation.data.lang),
        action.mutation,
        cursors[i].index
      );
      cursorsUpdated = true;
      if (cursors[i].index !== -1) cursors[i].index = movedWord.indexWord;
    }
  }
  newTranscript.cursors = cursors;
  if (cursorsUpdated) {
    newTranscript.cursors = [...newTranscript.cursors];
  }
  let updatedFurthestObservedCursors = { ...state.furthestObservedCursors };
  if (currentTouchedIndex && currentMutation.data.mutatorHash) {
    if (
      !furthestCursorForCurrentMutator ||
      currentTouchedIndex.tid > furthestCursorForCurrentMutator.tid ||
      (currentTouchedIndex.tid === furthestCursorForCurrentMutator.tid &&
        currentTouchedIndex.index > furthestCursorForCurrentMutator.index)
    ) {
      updatedFurthestObservedCursors[currentMutation.data.mutatorHash] = currentTouchedIndex;
    }
  }

  let newPrevSelectedWordIndex = state.ui.prevSelectedWordIndex;
  let newSelectedWordIndex = state.ui.selectedWordIndex;
  let newSelectedWordInputValue = state.ui.selectedWordInputValue;
  let newSelectedTranscriptIndex = state.ui.selectedTranscriptIndex;

  if (
    currentMutation.parent === 'lucy' &&
    currentMutation.type === 'insert' &&
    action.asr &&
    newPrevSelectedWordIndex != null &&
    newPrevSelectedWordIndex < words.length
  ) {
    newSelectedWordIndex = newPrevSelectedWordIndex;
    newSelectedWordInputValue = words[newSelectedWordIndex];
    newSelectedTranscriptIndex = newTranscript.id;
  } else if (
    action.asr &&
    state.ui.selectedTranscriptIndex === transcript.id &&
    getLang(newTranscript.speechLang, state.lang) === action.mutation.data.lang
  ) {
    if (typeof state.ui.selectedWordIndex === 'number') {
      newSelectedWordIndex = newIndexWord;
      newSelectedWordInputValue = words[newSelectedWordIndex];

      newSelectedWordIndex = Math.min(newSelectedWordIndex || 0, words.length - 1);
      newSelectedWordInputValue = words[newSelectedWordIndex];
    }
  }

  if (newSelectedWordIndex !== state.ui.selectedWordIndex) {
    newPrevSelectedWordIndex = state.ui.selectedWordIndex;
  }

  let langs: Array<string> = state.langs || [];
  let { lang } = state;
  if (action.mutation.type === 'insert' || action.mutation.type === 'delete') {
    const dlang = action.mutation.data.lang;
    if (!langs.includes(dlang)) langs = uniq([...langs, dlang]);
  }
  if (langs.length === 1) {
    [lang] = langs;
  }
  return {
    ...state,
    lang,
    langs,
    ui: {
      ...state.ui,
      prevSelectedWordIndex: newPrevSelectedWordIndex,
      selectedWordIndex: newSelectedWordIndex,
      selectedWordInputValue: newSelectedWordInputValue,
      selectedTranscriptIndex: newSelectedTranscriptIndex,
    },
    furthestObservedCursors: updatedFurthestObservedCursors,
    transcripts: {
      ...state.transcripts,
      [action.transcriptId]: {
        ...newTranscript,
        mutations: [{ ...action.mutation, timestamp: Date.now() }],
        mutationsQueuedByHumanMutation: [],
      },
    },
  };
};

type Action =
  | { type: 'SET_SPEECH_LANG', lang: string }
  | { type: 'SET_MIC_ID', micId: string }
  | { type: 'SET_ONLY_INTERNAL_AUDIO', value: boolean }
  | { type: 'SET_AVAILABLE_MICS', mics: Array<any> }
  | { type: 'START_RECORD' }
  | { type: 'USER_UPDATE', user: Object }
  | { type: 'STOP_RECORD' }
  | { type: 'FETCH_CONVERSATIONS', conversations: Array<Object> }
  | {
      type: 'FETCH_CONVERSATIONS_TRANSCRIPTS',
      roomId: string,
      transcripts: Array<Object>,
    }
  | { type: 'SWITCH_LANG', lang: string }
  | {
      type: 'RECEIVED_SCRIBE_CONVERSATION_TRANSCRIPT_MUTATION',
      transcriptId: string,
      origin: 'asr',
      mutation: ScribeTranscriptMutation,
      speakerId: ?string,
    }
  | { type: 'SHOW_OR_HIDE_NEW_TRANSCRIPT_INPUT', value: boolean }
  | {
      type: 'SCRIBE_UPDATE_SEARCH_QUERY',
      query: string,
    }
  | {
      type: 'RECEIVED_SCRIBE_SPEAKER_INFO',
      userName: string,
      userId: string,
      userAvaName: string,
      userAvatar: string,
      title?: string,
    }
  | { type: 'REJECT_SCRIBE_CONVERSATION_REQUEST' }
  | { type: 'ACCEPT_SCRIBE_CONVERSATION_REQUEST' }
  | { type: 'UPDATE_CONVERSATION_ENDED_STATUS', status: boolean }
  | { type: 'AVA_SESSION_CONNECTION_ACCEPT' }
  | { type: 'CLEAR_SCRIBE_CONVERSATION' }
  | { type: 'SCRIBE_CONVERSATION_TAGGED', tags: Object }
  | { type: 'CONNECTION_SUCCEEDED', status: string, reconnectionWsUrl: string }
  | { type: 'RECONNECTION_WS_USED' }
  | { type: 'SCRIBE_WORD_EDIT_STARTED' }
  | { type: 'SCRIBE_PREVIOUS_SEARCH_MATCH' }
  | { type: 'SCRIBE_NEXT_SEARCH_MATCH' }
  | { type: 'EXIT_SCRIBE_MODE' }
  | { type: 'SCRIBE_ENTER_SEARCH_MODE' }
  | { type: 'BOOSTING', isBoosting: boolean }
  | { type: 'SCRIBE_MULTI_SELECT', direction: number }
  | { type: 'UPDATE_SPEAKER_LISTENED', speaker: string }
  | { type: 'SCRIBE_SET_LAST_EDITED_WORD_INDEX', lastEditedWordIndex: number }
  | {
      type: 'SCRIBE_TRANSCRIPT_UPDATE_WORD_INPUT_VALUE',
      inputValue: string,
      caret: string,
    }
  | { type: 'CLEAN_APP' }
  | { type: 'CONSUME_RECONNECTION_WS_URL' }
  | { type: 'SCRIBE_TRANSCRIPT_DESELECT_WORD' }
  | { type: 'ROOM_STATUS_UPDATE', status: Object, force?: boolean }
  | {
      type: 'SCRIBE_TRANSCRIPT_SELECT_WORD',
      transcriptId: string,
      index: number,
      caret: string,
      wordText: string,
    }
  | {
      type: 'RECEIVED_SCRIBE_CONVERSATION_END_MESSAGE',
      success?: boolean,
      msg?: string,
    }
  | {
      type: 'FETCH_AVA_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      avaId: string,
      roomId: string,
      support: Array<string>,
    }
  | {
      type: 'FETCH_SCRIBE_SESSION_SUCCESS',
      url: string,
      captioner: ScribeCaptioner,
      avaId: string,
      roomId: string,
      support: Array<string>,
    }
  | { type: 'SCRIBE_LOST_CONNECTION_MESSAGE' }
  | { type: 'LOST_WEB_SESSION_CONNECTION' }
  | { type: 'SET_LOADED' }
  | { type: 'SET_NOT_LOADED' }
  | { type: 'UPDATE_IS_IN_CONVERSATION', value: boolean }
  | {
      type: 'SCRIBE_CREATE_TRANSCRIPT',
      id: string,
      mutation: ScribeTranscriptMutation,
      author: string,
      lang: string,
    }
  | {
      type: 'UPDATE_BROADCAST_TO_ZOOM_TOKEN',
      token: string,
    }
  | {
      type: 'SET_CURSE_FILTER',
      value: number,
    }
  | { type: 'ADD_EDITED_WORD', payload: { text: string, transcriptionId: string } };

const availableCodeLanguage: Array<string> = Object.values(availableLangs).map(x => {
  if (x && x.languageCode && typeof x.languageCode === 'string') {
    return x.languageCode;
  }
  return 'en-US';
});
const navLang = window.navigator.userLanguage || window.navigator.language;
const getInitialState = () => ({
  allMicsSelected: [],
  availableLangs,
  availableMics: [],
  backendKnowUpdateField: false,
  lang: window.location.href.includes('fii.ava.me/session-') ? 'en' : '~',
  langs: ['~'],
  transcripts: {},
  speakers: {},
  tags: {},
  micIdSelected: undefined,
  muted: false,
  transcriptsFinal: [],
  transcriptsCurrent: [],
  furthestObservedCursors: {},
  conversationInputIsEdited: false,
  recording: false,
  isBoosting: false,
  isInConversation: false,
  inTwilioCalls: false,
  hasRecorded: false,
  token: undefined,
  speechLang:
    localStorage.getItem('speechLang') ||
    (availableCodeLanguage.includes(navLang)
      ? navLang
      : availableCodeLanguage.find((lang: string) => lang.substring(0, navLang.length) === navLang) || 'en-US'),
  status: {},
  scribe: '',
  speakerListened: 'host',
  loaded: false,
  ui: {
    selectedSearchMatchIndex: 0,
    selectionLength: 0,
    lastEditedWordIndex: -1,
    keyboardInputShown: false,
    prevSelectedWordIndex: undefined,
  },
  conversations: [],
  curseFilter: localStorage.getItem('ava-curse-filter') ? parseInt(localStorage.getItem('ava-curse-filter'), 10) : 2,
  onlyInternalAudio: false,
  editedWords: [],
  isInviteCardOpen: false,
  joiningStatus: 'none',
});
/* eslint-enable global-require */

const initialState = getInitialState();

if (getSearchValue(window, 'token', undefined)) {
  initialState.token = getSearchValue(window, 'token', undefined);
  initialState.scribe = getSearchValue(window, 'scribe', undefined);
}

const applyQueuedMutationsIfPossible = (state: State, transcriptId?: string): State => {
  if (!transcriptId) {
    return state;
  }
  const parentSpeechBloc: ?Transcript = state.transcripts[transcriptId];

  if (!parentSpeechBloc) return { ...state };
  if (state.conversationInputIsEdited) {
    return { ...state };
  }

  (parentSpeechBloc.mutationsQueuedByHumanMutation || []).forEach(mutation => {
    // eslint-disable-next-line no-param-reassign
    state = mutate(state, { mutation, transcriptId, asr: true });
  });

  return {
    ...state,
  };
};

function scribeConversation(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'SET_SPEECH_LANG': {
      return {
        ...state,
        speechLang: action.lang,
      };
    }

    case 'SET_AVAILABLE_MICS': {
      return {
        ...state,
        availableMics: action.mics,
      };
    }

    case 'BOOSTING': {
      return {
        ...state,
        isBoosting: action.isBoosting,
      };
    }

    case 'SET_MIC_ID': {
      const { allMicsSelected, availableMics, micIdSelected } = state;
      const { micId } = action;
      const micSelected = availableMics && availableMics.find(m => m.value === micId);
      const newAllMicsSelected = [...allMicsSelected];
      if (micSelected && micIdSelected !== action.micId) {
        newAllMicsSelected.push(micSelected.label);
      }
      return {
        ...state,
        micIdSelected: action.micId,
        allMicsSelected: newAllMicsSelected,
      };
    }
    case 'SET_ONLY_INTERNAL_AUDIO': {
      return {
        ...state,
        onlyInternalAudio: action.value,
      };
    }

    case 'START_RECORD': {
      return {
        ...state,
        recording: true,
        hasRecorded: true,
      };
    }

    case 'STOP_RECORD': {
      return {
        ...state,
        recording: false,
      };
    }

    case 'FETCH_CONVERSATIONS': {
      return {
        ...state,
        conversations: action.conversations,
      };
    }

    case 'FETCH_CONVERSATIONS_TRANSCRIPTS': {
      return {
        ...state,
        conversations: state.conversations.map(c =>
          // $FlowFixMe
          c.id !== action.roomId ? c : { ...c, transcripts: action.transcripts }
        ),
      };
    }

    case 'CLEAR_SCRIBE_CONVERSATION': {
      return {
        ...getInitialState(),
      };
    }
    case 'CLEAN_APP': {
      return {
        ...getInitialState(),
        conversations: state.conversations,
      };
    }

    case 'AVA_SESSION_CONNECTION_ACCEPT': {
      return {
        ...state,
        conversationStarted: true,
      };
    }

    case 'SCRIBE_WORD_EDIT_STARTED': {
      return {
        ...state,
        conversationInputIsEdited: true,
      };
    }

    case 'UPDATE_SPEAKER_LISTENED': {
      return {
        ...state,
        speakerListened: action.speaker,
      };
    }

    case 'RECEIVED_SCRIBE_SPEAKER_INFO': {
      const { userId, userName, userAvaName, userAvatar, title } = action;
      const randomColor = randomMC.getColor({ text: userId });

      const newSpeaker = {
        avaId: userId,
        id: userId,
        userName,
        avaName: userAvaName,
        userAvatar: userAvatar || '',
        title,
      };

      if (state.speakers[userId]?.theme && isMatch(state.speakers[userId], newSpeaker)) return state;
      // Sometimes the speaker info for a user will not have a theme.
      // When this happens, use a randomly generated color which will map
      // to the speaker.
      // $FlowFixMe
      newSpeaker.theme = state.speakers[userId]?.theme || { dark_theme: randomColor, light_theme: randomColor };

      return {
        ...state,
        speakers: {
          ...state.speakers,
          [userId]: {
            ...state.speakers[userId],
            ...newSpeaker,
          },
        },
      };
    }

    case 'SCRIBE_TRANSCRIPT_SELECT_WORD': {
      return applyQueuedMutationsIfPossible(
        {
          ...state,
          conversationInputIsEdited: false,
          ui: {
            ...state.ui,
            selectionLength: 0,
            selectedTranscriptCaretPosition: action.caret || 'all',
            prevSelectedWordIndex: state.ui.selectedWordIndex,
            selectedWordIndex: action.index,
            selectedTranscriptIndex: action.transcriptId,
            selectedWordInputValue: action.wordText,
          },
        },
        state.ui.selectedTranscriptIndex
      );
    }

    case 'SCRIBE_TRANSCRIPT_DESELECT_WORD': {
      return applyQueuedMutationsIfPossible(
        {
          ...state,
          conversationInputIsEdited: false,
          ui: {
            ...state.ui,
            selectionLength: 0,
            prevSelectedWordIndex: undefined,
            selectedWordIndex: undefined,
            selectedTranscriptCaretPosition: 'none',
            selectedTranscriptIndex: undefined,
            selectedIndexFrom: undefined,
            selectedIndexTo: undefined,
          },
        },
        state.ui.selectedTranscriptIndex
      );
    }

    case 'REJECT_SCRIBE_CONVERSATION_REQUEST':
      return { ...state, conversationRequested: false };

    case 'RECONNECTION_WS_USED': {
      return {
        ...state,
        reconnectionWsUrl: undefined,
      };
    }

    case 'CONNECTION_SUCCEEDED': {
      return {
        ...state,
        connectionHash: action.status,
        lastMutationReceivedTimestampMs: Date.now(),
        reconnectionWsUrl: action.reconnectionWsUrl,
      };
    }

    case 'ACCEPT_SCRIBE_CONVERSATION_REQUEST':
      return { ...state, conversationStarted: true };

    case 'EXIT_SCRIBE_MODE':
      return { ...state, scribe: undefined, token: undefined };

    case 'RECEIVED_SCRIBE_CONVERSATION_TRANSCRIPT_MUTATION': {
      const { transcriptId, mutation, origin } = action;
      // eslint-disable-next-line
      let { speakerId } = action;
      if (mutation.type === 'changeSpeaker') {
        ({ speakerId } = mutation.data);
      }
      let { transcriptsCurrent } = state;
      let { transcriptsFinal } = state;
      const parentSpeechBloc = state.transcripts[transcriptId];

      if (state.transcriptsFinal.includes(transcriptId) && mutation.parent === 'lucy') {
        transcriptsCurrent = [...transcriptsCurrent, transcriptId].sort(
          // $FlowFixMe
          (a, b) => a - b
        );
        transcriptsFinal = transcriptsFinal.filter(tid => tid !== transcriptId);
        parentSpeechBloc.mutationsQueuedByHumanMutation = [];
        parentSpeechBloc.mutations = [];
        parentSpeechBloc.texts = {};
      }
      if (
        parentSpeechBloc &&
        mutation.parent !== 'lucy' &&
        last(parentSpeechBloc.mutations) &&
        last(parentSpeechBloc.mutations).id !== mutation.parent &&
        (!last(parentSpeechBloc.mutationsQueuedByHumanMutation) ||
          last(parentSpeechBloc.mutationsQueuedByHumanMutation).id !== mutation.parent)
      ) {
        return state;
      }

      const mutationWithTimestamp = {
        ...mutation,
        origin,
        timestamp: Date.now(),
      };

      let { backendKnowUpdateField } = state;
      if (mutation.type === 'updateField' && origin !== 'human') {
        backendKnowUpdateField = true;
      }

      if (action.mutation.type === 'insert' || action.mutation.type === 'delete') {
        // eslint-disable-next-line no-param-reassign
        action.mutation.data.lang = action.mutation.data.lang || state.lang;
      }

      // Don't apply ASR mutation when user edit input in this transcript
      if (state.conversationInputIsEdited && state.ui.selectedTranscriptIndex === transcriptId && origin !== 'human') {
        return {
          ...state,
          transcripts: {
            ...state.transcripts,
            [transcriptId]: {
              ...parentSpeechBloc,
              mutationsQueuedByHumanMutation: [...(parentSpeechBloc.mutationsQueuedByHumanMutation || []), mutation],
            },
          },
          transcriptsCurrent,
          transcriptsFinal,
        };
      }
      if (!parentSpeechBloc) {
        const lastTranscript = state.transcripts[last(state.transcriptsCurrent)];

        const authorShow = !lastTranscript || (speakerId && lastTranscript.author !== speakerId);

        return mutate(
          {
            ...state,
            backendKnowUpdateField,
            transcriptsCurrent: [...state.transcriptsCurrent, transcriptId].sort(
              // $FlowFixMe
              (a, b) => a - b
            ),
            lastMutationReceived: Date.now(),
            transcripts: {
              ...state.transcripts,
              [transcriptId]: {
                id: transcriptId,
                isFinal: false,
                index: 0,
                author: speakerId || mutation.data.speakerId || 'Callee',
                authorShow,
                origin,
                mutationsQueuedByHumanMutation: [],
                mutations: [mutationWithTimestamp],
                speechLang: state.lang,
                texts: {
                  [state.lang]: '',
                },
              },
            },
          },
          {
            mutation: action.mutation,
            transcriptId,
            asr: action.origin === 'asr',
          }
        );
      }

      if (!parentSpeechBloc.mutations) {
        return state;
      }

      return updateAuthorShow(
        mutate(
          {
            ...state,
            backendKnowUpdateField,
            transcriptsCurrent,
            transcriptsFinal,
            lastMutationReceived: Date.now(),
          },
          {
            mutation: action.mutation,
            transcriptId: action.transcriptId,
            asr: action.origin === 'asr',
          }
        )
      );
    }

    case 'UPDATE_IS_IN_CONVERSATION': {
      const newState = { ...state, isInConversation: action.value };
      if (action.value && !state.isInConversation) {
        // Resetting the list of mics from this conversation
        newState.allMicsSelected = [];
        if (state.micIdSelected) {
          const mic = state.availableMics && state.availableMics.find(m => m.value === state.micIdSelected);
          if (mic) {
            newState.allMicsSelected.push(mic.label);
          }
        }
      }
      return newState;
    }

    case 'UPDATE_CONVERSATION_ENDED_STATUS':
      return {
        ...state,
        conversationEnded: action.status,
      };

    case 'RECEIVED_SCRIBE_CONVERSATION_END_MESSAGE':
      return {
        ...state,
        conversationEnded: true,
        conversationEndedSuccessfully: action.success,
        conversationEndedMessage: action.msg,
      };

    case 'SCRIBE_LOST_CONNECTION_MESSAGE':
      return { ...state, connectionLost: true };

    case 'SCRIBE_CREATE_TRANSCRIPT': {
      const newState = {
        ...state,
        transcripts: {
          ...state.transcripts,
          [action.id]: {
            id: action.id,
            isFinal: false,
            origin: 'human',
            author: action.author || 'Callee',
            authorShow: false,
            mutationsQueuedByHumanMutation: [],
            mutations: [action.mutation],
            speechLang: action.lang,
            texts: {
              [action.lang]: '',
            },
          },
        },
        transcriptsCurrent: [...state.transcriptsCurrent, action.id].sort(
          // $FlowFixMe
          (a, b) => a - b
        ),
      };

      return updateAuthorShow(
        mutate(
          {
            ...newState,
          },
          {
            mutation: action.mutation,
            transcriptId: action.id,
            asr: false,
          }
        )
      );
    }

    case 'CONSUME_RECONNECTION_WS_URL': {
      return {
        ...state,
        reconnectionWsUrl: undefined,
      };
    }

    case 'SCRIBE_CONVERSATION_TAGGED': {
      return {
        ...state,
        tags: action.tags,
      };
    }

    case 'SCRIBE_NEXT_SEARCH_MATCH':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedSearchMatchIndex: state.ui.selectedSearchMatchIndex + 1,
        },
      };

    case 'SCRIBE_PREVIOUS_SEARCH_MATCH':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedSearchMatchIndex: state.ui.selectedSearchMatchIndex - 1,
        },
      };

    case 'SCRIBE_MULTI_SELECT': {
      const parentTranscript = state.transcripts[state.ui.selectedTranscriptIndex || ''];

      if (!parentTranscript || state.ui.selectedWordIndex === undefined) return { ...state };

      let [indexFirstWord, indexLastWord] = orderBy([
        state.ui.selectedWordIndex,
        state.ui.selectedWordIndex + state.ui.selectionLength,
      ]);

      if (action.direction === 1) {
        indexLastWord = Math.min(indexLastWord + 1, getText(parentTranscript, state.lang).split(' ').length - 1);
      }
      if (action.direction === -1) {
        indexFirstWord = Math.max(indexFirstWord - 1, 0);
      }

      const newSelectionText = `${getText(parentTranscript, state.lang)
        .split(' ')
        .slice(indexFirstWord, indexLastWord + 1)
        .join(' ')}`;

      return {
        ...state,
        ui: {
          ...state.ui,
          selectionLength: indexLastWord - indexFirstWord,
          selectedWordIndex: indexFirstWord,
          selectedWordInputValue: newSelectionText,
          wordBeingEdited: false,
        },
      };
    }

    case 'SCRIBE_SET_LAST_EDITED_WORD_INDEX':
      return {
        ...state,
        ui: {
          ...state.ui,
          lastEditedWordIndex: Math.max(action.lastEditedWordIndex, state.ui.lastEditedWordIndex),
        },
      };

    case 'SCRIBE_TRANSCRIPT_UPDATE_WORD_INPUT_VALUE':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedWordInputValue: action.inputValue,
          selectedTranscriptCaretPosition: action.caret,
        },
      };

    case 'SCRIBE_ENTER_SEARCH_MODE':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedWordIndex: undefined,
        },
      };

    case 'SCRIBE_UPDATE_SEARCH_QUERY':
      return {
        ...state,
        ui: {
          ...state.ui,
          searchQuery: action.query,
          selectedSearchMatchIndex: 0,
        },
      };

    case 'SWITCH_LANG':
      return {
        ...state,
        lang: action.lang,
      };

    case 'ROOM_STATUS_UPDATE': {
      // TODO: This is an ugly hack, but it's the easiest way to get the
      // avaId of the current user. Optimally the whole logic around `muted`
      // variable should be refactored into a selector.
      const currentAvaId = localStorage.getItem('avaId');
      const muted =
        action.status.conversationMode === 'public-muted' &&
        !state.scribe &&
        (!currentAvaId || state.status.host.avaId !== currentAvaId);

      let { speakers } = state;
      (action.status.speakers || []).forEach(speaker => {
        if (isEqual(speaker.theme, speakers[speaker.avaId]?.theme)) return;
        speakers = {
          ...speakers,
          [speaker.avaId]: {
            ...speakers[speaker.avaId],
            theme: speaker?.theme,
          },
        };
      });
      const newRoom = action.status.id !== state.status.id;
      const status = {
        ...(action.force ? {} : state.status),
        ...action.status,
      };
      let newLangs = uniq(state.langs.concat(action.status.langs || []));
      if (newLangs.length === state.langs.length) {
        // newLangs must contain all languages of state.langs, becuase it is
        // concatenated. So if the length is the same - it means all the elements
        // are the same. So we don't change the field, so as to not trigger a rerender.
        newLangs = state.langs;
      }
      return {
        ...state,
        speakers,
        inTwilioCalls: status.twilioCalls && Object.keys(status.twilioCalls).length > 0,
        status,
        langs: newLangs,
        lang: state.langs.length === 0 ? action.status.langs[0] : state.lang,
        muted,
        ui: {
          ...state.ui,
          keyboardInputShown: state.status.conversationId && !muted && state.ui.keyboardInputShown,
        },
        transcripts: newRoom ? {} : state.transcripts,
        transcriptsFinal: newRoom ? [] : state.transcriptsFinal,
        transcriptsCurrent: newRoom ? [] : state.transcriptsCurrent,
        speakerListened: newRoom ? undefined : state.speakerListened,
      };
    }

    case 'FETCH_AVA_SESSION_SUCCESS':
      return {
        ...state,
        backendKnowUpdateField: state.backendKnowUpdateField || (action.support || []).includes('updateField'),
      };

    case 'FETCH_SCRIBE_SESSION_SUCCESS':
      return {
        ...state,
        backendKnowUpdateField: state.backendKnowUpdateField || (action.support || []).includes('updateField'),
      };

    case 'LOST_WEB_SESSION_CONNECTION':
      return {
        ...state,
        conversationInputIsEdited: false,
        ui: {
          selectedSearchMatchIndex: 0,
          selectionLength: 0,
          lastEditedWordIndex: -1,
          keyboardInputShown: state.ui.keyboardInputShown,
        },
      };

    case 'SHOW_OR_HIDE_NEW_TRANSCRIPT_INPUT':
      return {
        ...state,
        ui: {
          ...state.ui,
          keyboardInputShown: !state.muted && action.value,
        },
      };

    case 'USER_UPDATE':
      if (!action.user || action.user.userName === state.speakers[action.user.avaId]?.userName) {
        return state;
      }
      return {
        ...state,
        speakers: {
          ...state.speakers,
          [action.user.avaId]: {
            ...state.speakers[action.user.avaId],
            userName: action.user.userName,
          },
        },
      };

    case 'SET_LOADED':
      return {
        ...state,
        loaded: true,
      };

    case 'SET_NOT_LOADED':
      return {
        ...state,
        loaded: false,
      };

    case 'UPDATE_BROADCAST_TO_ZOOM_TOKEN':
      return {
        ...state,
        broadcastToZoomToken: action.token,
      };

    case 'SET_CURSE_FILTER':
      window.localStorage.setItem('ava-curse-filter', action.value);
      return {
        ...state,
        curseFilter: action.value,
      };

    case 'ADD_EDITED_WORD':
      return {
        ...state,
        editedWords: [...(state.editedWords || []), action.payload],
      };

    case ConversationActionTypes.SET_INVITE_CARD_OPEN:
      return {
        ...state,
        isInviteCardOpen: action.payload,
      };

    case ConversationActionTypes.SET_JOINING_STATUS:
      return {
        ...state,
        joiningStatus: action.payload,
      };

    default:
      // eslint-disable-next-line no-unused-expressions
      (action: empty);
      return state;
  }
}

export default function reducerWrapper(state: State = initialState, action: Action): State {
  const newState = scribeConversation(state, action);
  if (!isProd) assertScribeConversationStateConsistency(newState);
  return newState;
}
